import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Spin } from 'antd';
import { AllPermissions } from "../../model/auth/Permissions";
import { useMember } from '../member/MemberContext';

type PermissionType = typeof AllPermissions;
type PermissionKeys = keyof PermissionType;
type PermissionValues = PermissionType[PermissionKeys];

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredPermission?: PermissionValues[];
    /** When true, redirects to /unauthorized if the authenticated user has no
     *  linked member record (i.e. permissions.member.membershipId is absent). */
    requireMember?: boolean;
    /** When true, the route is accessible if the user has any of the required
     *  permissions OR does NOT have a membershipId (i.e. they still need to sign up).
     *  Used for the new member registration form. */
    allowWithoutMember?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
                                                                  children,
                                                                  requiredPermission: requiredPermissions,
                                                                  requireMember,
                                                                  allowWithoutMember
                                                              }) => {
    const { isAuthenticated, isLoading: authLoading } = useAuth0();
    const { isLoading: permissionsLoading, permissions } = useMember();
    const location = useLocation();

    console.log('Checking permissions for endpoint', location.pathname,
        'with required permission', requiredPermissions,
        'current permissions', permissions);

    if (authLoading || permissionsLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Spin size="large" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check if the user has the required permission or permissions
    if (requiredPermissions) {
        const hasPermission = requiredPermissions.some(reqPerm =>
            permissions?.permissions.some(p => p.name === reqPerm)
        );

        // If allowWithoutMember is set, also allow access if user has no membershipId (needs to sign up)
        const hasMemberAccess = allowWithoutMember && !permissions?.member?.membershipId;

        if (!hasPermission && !hasMemberAccess) {
            console.log('Permission denied:', requiredPermissions);
            return <Navigate to="/unauthorized" replace />;
        }
    }

    // A member record is normally required, but allowWithoutMember lets a
    // not-yet-member through (e.g. the self-service onboarding create state on
    // /my-profile?onboarding=1). Non-onboarding no-member visitors are still
    // handled downstream by the screen (self-service load bounces them home).
    if (requireMember && !permissions?.member?.membershipId && !allowWithoutMember) {
        console.log('Member record required but not found — redirecting to /unauthorized');
        return <Navigate to="/unauthorized" replace />;
    }

    return <>{children}</>;
};